import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import type { TranslationLang } from "@/content/translation-langs/registry";
import { TRANSLATION_COUNTRY_NAMES as TRANSLATION_COUNTRY_LANDINGS, type TranslationCountryName } from "@/content/translation-corpus/names.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

type DocLink = { slug: string; titleTh: string };

export const Route = createFileRoute("/translation/")({
  loader: async (): Promise<{ langs: TranslationLang[]; docs: DocLink[] }> => {
    const [{ TRANSLATION_LANGS }, { TRANSLATION_DOC_LANDINGS }] = await Promise.all([
      import("@/content/translation-langs/registry"),
      import("@/content/translation-corpus/documents"),
    ]);
    return {
      langs: TRANSLATION_LANGS,
      docs: TRANSLATION_DOC_LANDINGS.map((x) => ({ slug: x.slug, titleTh: x.titleTh })),
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "แปลรับรอง 8 ภาษาหลัก — Notary + MFA + สถานทูต",
      description:
        "บริการแปลรับรองนิติกรณ์ ภาษาจีน ญี่ปุ่น เกาหลี เยอรมัน ฝรั่งเศส สเปน รัสเซีย อาหรับ ส่งงาน 1-5 วัน รับ MFA + สถานทูตในจุดเดียว — เริ่ม 500 บาท/หน้า",
      path: "/translation",
      alternates: [{ lang: "en", path: "/en/translation" }],
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/translation")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Translation Languages", url: `${site}/translation` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Certified Translation Languages",
            itemListElement: (loaderData?.langs ?? []).map((l, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/translation/${l.slug}`,
              name: `${l.nameTh} (${l.nameEn})`,
            })),
          }),
        },
      ],
    };
  },
  component: TranslationIndex,
});

function TranslationIndex() {
  const { langs, docs } = Route.useLoaderData() as {
    langs: TranslationLang[];
    docs: DocLink[];
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Languages className="mr-1 inline h-3.5 w-3.5" /> Certified Translation
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              แปลรับรอง 8 ภาษาหลัก — ส่งงาน 1-5 วัน
            </h1>
            <p className="mt-4 max-w-3xl text-base text-muted-foreground">
              เลือกภาษาที่คุณต้องการ เพื่อดูราคา เอกสารที่รับแปล หน่วยงานปลายทางที่ยอมรับ และวิธีรับรองรวม MFA + สถานทูตในจุดเดียว
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="grid gap-4 md:grid-cols-2">
              {langs.map((l) => (
                <Link
                  key={l.slug}
                  to="/translation/$lang"
                  params={{ lang: l.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-3xl leading-none">{l.flag}</div>
                      <h2 className="mt-3 text-lg font-bold text-primary group-hover:text-gold">
                        แปล{l.nameTh} ({l.nameEn})
                      </h2>
                      <div className="mt-1 text-xs text-muted-foreground">{l.nativeName}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-gold" />
                  </div>
                  <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{l.intro}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-gold/10 px-2.5 py-1 font-bold text-gold">
                      เริ่ม ฿{l.pricePerPage.from.toLocaleString()}/หน้า
                    </span>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">{l.turnaround}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
        <section className="container mx-auto max-w-5xl px-4 pb-12">
          <h2 className="mb-3 text-xl font-bold">แปลรับรองตามประเภทเอกสาร</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            เลือกประเภทเอกสารเพื่อดูรายการที่ต้องเตรียม ระบบรับรองคำแปลที่ประเทศปลายทางกำหนด และขั้นตอนนิติกรณ์
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {docs.map((x) => (
              <a
                key={x.slug}
                href={`/translation/document/${x.slug}`}
                className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted"
              >
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>
        <section className="container mx-auto max-w-5xl px-4 pb-12">
          <h2 className="mb-3 text-xl font-bold">แปลรับรองตามประเทศปลายทาง</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            แต่ละประเทศกำหนดระบบผู้แปลไม่เหมือนกัน (NAATI, sworn translator, traductor jurado, Wbtv ฯลฯ)
            เลือกประเทศปลายทางเพื่อดูระบบที่ยอมรับ ลำดับนิติกรณ์ที่ถูกต้อง และสาเหตุที่ถูกตีกลับ
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_COUNTRY_LANDINGS.map((x: TranslationCountryName) => (
              <a
                key={x.slug}
                href={`/translation/country/${x.slug}`}
                className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted"
              >
                {x.nameTh}
              </a>
            ))}
          </div>
        </section>
        <section className="container mx-auto max-w-5xl px-4 pb-12">
          <FaqSourceNote topic="translation" locale="th" />
        </section>
        <div className="mb-10">
          <PageGallery category="translation" seed="translation-index" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}
